import { useMemo } from "react";
import {
  requireFullUserSnapshots,
  type HubUserCredentials,
  type PriceEventDto,
  type PriceEventSeriesDto,
  type UserSnapshotsDto,
} from "@cede/types";
import { API_ENDPOINT, QueryKeys, type ApiHelper } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import type { HydratedUserSnapshots } from "@/utils/pnl/pnl-chart";
import { buildInitialHydrationState, type PnlHydrationState } from "@/utils/pnl/pnl-snapshot-reducer";
import { useQuery, type QueryClient } from "@tanstack/react-query";

export const MAX_LIMIT = 50;

/**
 * The query cache stores the full hydration state so incremental SSE updates
 * can re-run the reducer (`applyTradeSnapshot` / `applyPriceEvent`) against
 * the previous state without touching a separate store. `userCount` and
 * `rank` are pulled from the `/me` endpoint in the same queryFn.
 */
export type PnlSnapshotQueryData = {
  hydrationState: PnlHydrationState;
  userCount: number;
  rank: number | null;
};

export const getPnlSnapshotQueryKey = (campaignName: string, epochId: number, exchangeId?: string) =>
  exchangeId === undefined
    ? [QueryKeys.PNL_SNAPSHOTS, campaignName, epochId]
    : [QueryKeys.PNL_SNAPSHOTS, campaignName, epochId, exchangeId];

async function fetchSnapshots(apiHelper: ApiHelper, campaignName: string, epochId: number) {
  return apiHelper.getOrThrow(API_ENDPOINT.PNL_SNAPSHOTS, {
    queryParams: { campaign: campaignName, epoch: String(epochId), limit: String(MAX_LIMIT) },
  });
}

async function fetchPriceEvents(apiHelper: ApiHelper, exchangeId: string, pairSymbols: string[], from: Date, to: Date) {
  return apiHelper.getOrThrow(API_ENDPOINT.PNL_PRICE_EVENTS, {
    queryParams: {
      from: from.toISOString(),
      to: to.toISOString(),
      exchangeId,
      // Comma-separated — matches the backend DTO's `split(",")` contract.
      // Scoping by pair lets Postgres hit the composite index directly.
      pairSymbols: pairSymbols.join(","),
    },
  });
}

/**
 * Expand the columnar per-pair wire shape back into the flat `PriceEventDto[]`
 * the reducer consumes. The bulk endpoint collapses events into
 * `{ pairSymbol, prices, timestamps }` series to keep the payload small — at
 * 12h × 5s cadence that's ~8640 events per pair, and the object-key overhead
 * of a per-event shape dominates gzip's wins.
 */
const expandPriceEventSeries = (series: PriceEventSeriesDto[]): PriceEventDto[] => {
  const events: PriceEventDto[] = [];
  for (const { pairSymbol, prices, timestamps } of series) {
    // The two arrays are guaranteed parallel on the server; defend anyway so a
    // corrupt response can't silently drop points or array-index past the end.
    const len = Math.min(prices.length, timestamps.length);
    for (let i = 0; i < len; i++) {
      events.push({
        pairSymbol,
        priceUSD: prices[i]!,
        createdAt: new Date(timestamps[i]!),
      });
    }
  }
  return events;
};

async function fetchMe(apiHelper: ApiHelper, auth: HubUserCredentials, campaignName: string, epochId: number) {
  return apiHelper.getWithAuth(auth, API_ENDPOINT.USER_PNL_STATS, {
    queryParams: { campaign: campaignName, epoch: String(epochId) },
  });
}

/**
 * Merge the current user's own snapshots (from `/me`) into the top-users
 * list when they aren't already in the top list. The merge preserves any
 * extra rows the top-users endpoint doesn't return (the user may be outside
 * the top 50 but still need their PnL surfaced in the chart / stats card).
 *
 * The synthesized "me" entry carries the nickname from `useHubUser()`: the `/me`
 * PnL endpoint doesn't project it, so without this passthrough the leaderboard
 * falls back to the truncated wallet for the current user.
 */
const mergeMeIntoUsers = (
  topUsers: UserSnapshotsDto[],
  meSnapshots: UserSnapshotsDto["snapshots"] | undefined,
  defiAddress: string | undefined,
  nickname: string | null | undefined,
  hubUserId: number | undefined,
): UserSnapshotsDto[] => {
  if (!defiAddress || !meSnapshots?.length || hubUserId == null) return topUsers;
  const isInTop = topUsers.some((u) => u.defiAddress.toLowerCase() === defiAddress.toLowerCase());
  if (isInTop) return topUsers;
  return [...topUsers, { defiAddress, hubUserId, nickname: nickname ?? null, snapshots: meSnapshots }];
};

export const usePnlSnapshot = (
  campaignName: string,
  epoch?: { id: number; start: Date; end: Date },
  exchangeId?: string,
  pairSymbols?: string[],
) => {
  const apiHelper = useApi();
  const { auth, authenticated, hubUser, isAuthStateStable, isLoadingHubUser } = useHubUser();
  const epochId = epoch?.id;
  const hasPairSymbols = !!pairSymbols?.length;

  const { isLoading, data, error } = useQuery<PnlSnapshotQueryData>({
    queryKey:
      epochId != null
        ? [
            ...getPnlSnapshotQueryKey(campaignName, epochId, exchangeId),
            // Include the pair list in the cache key so the query refires
            // cleanly when a campaign's pairs change (rare, but cheap to be
            // correct). Sorted for stable key regardless of caller ordering.
            [...(pairSymbols ?? [])].sort().join(","),
            hubUser?.defiAddress ?? "",
            // `mergeMeIntoUsers` reads `hubUser?.nickname` — staleTime is Infinity
            // here, so without nickname in the key the synthesized "me" row would
            // keep an old/null nickname after the user claims one.
            hubUser?.nickname ?? "",
          ]
        : [],
    enabled:
      !!campaignName && epochId != null && !!exchangeId && hasPairSymbols && isAuthStateStable && !isLoadingHubUser,
    staleTime: Infinity,
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
    queryFn: async () => {
      if (epochId == null || !epoch) throw new Error("epoch required");
      if (!exchangeId) throw new Error("exchangeId required");
      if (!pairSymbols?.length) throw new Error("pairSymbols required");

      const snapshotsPromise = fetchSnapshots(apiHelper, campaignName, epochId);
      // Clamp the price-event window to the rolling 12h surfaced by the chart:
      //   end   = min(epoch.end, now)              — finished epochs don't replay
      //                                              post-competition ticks
      //   start = max(end - 12h, epoch.start)      — keep the window ≤ 12h and
      //                                              within the epoch bounds.
      //                                              Anchored to `end`, not `now`,
      //                                              so finished epochs whose end
      //                                              is >12h in the past still
      //                                              satisfy `from ≤ to`.
      // The backend enforces the same 12h cap (see get-market-price-snapshots.dto.ts).
      const now = new Date();
      const endDate = epoch.end.getTime() < now.getTime() ? epoch.end : now;
      const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
      const windowStart = new Date(endDate.getTime() - TWELVE_HOURS_MS);
      const startDate = windowStart.getTime() < epoch.start.getTime() ? epoch.start : windowStart;
      const priceEventsPromise = fetchPriceEvents(apiHelper, exchangeId, pairSymbols, startDate, endDate);
      const mePromise = authenticated
        ? fetchMe(apiHelper, auth, campaignName, epochId)
        : Promise.resolve({ userCount: 0, rank: null, snapshots: [] as UserSnapshotsDto["snapshots"] });

      const [snapshotsResp, priceEventsResp, meResp] = await Promise.all([
        snapshotsPromise,
        priceEventsPromise,
        mePromise,
      ]);

      const mergedUsers = mergeMeIntoUsers(
        requireFullUserSnapshots(snapshotsResp.users),
        meResp.snapshots,
        hubUser?.defiAddress ?? undefined,
        hubUser?.nickname,
        hubUser?.id,
      );
      const priceEvents = expandPriceEventSeries(priceEventsResp.priceEvents);
      const hydrationState = buildInitialHydrationState(mergedUsers, priceEvents);

      return {
        hydrationState,
        userCount: meResp.userCount ?? 0,
        rank: meResp.rank ?? null,
      };
    },
  });

  // Derive the component-facing shape (`users: HydratedUserSnapshots[]`) from
  // the cached Map without mutating it. Memoised so components re-render only
  // when the underlying hydration state reference changes.
  const users: HydratedUserSnapshots[] | undefined = useMemo(() => {
    if (!data?.hydrationState) return undefined;
    return Array.from(data.hydrationState.users.values()).map((u) => ({
      defiAddress: u.defiAddress,
      nickname: u.nickname,
      snapshots: u.snapshots,
    }));
  }, [data?.hydrationState]);

  const currentUserSnapshots = useMemo(() => {
    if (!data?.hydrationState || !hubUser?.defiAddress) return undefined;
    return data.hydrationState.users.get(hubUser.defiAddress.toLowerCase())?.snapshots;
  }, [data?.hydrationState, hubUser?.defiAddress]);

  return {
    users,
    currentUserSnapshots,
    currentUserDefiAddress: hubUser?.defiAddress ?? undefined,
    userCount: Math.max(data?.userCount ?? 0, data?.hydrationState?.users.size ?? 0),
    rank: data?.rank ?? null,
    isLoading,
    error,
  };
};

/** Invalidate the merged snapshot query — used by SSE handlers that need a full refetch. */
export function invalidatePnlSnapshot(
  queryClient: QueryClient,
  params: { campaignName: string; epochId: number; exchangeId?: string },
) {
  return queryClient.invalidateQueries({
    queryKey: getPnlSnapshotQueryKey(params.campaignName, params.epochId, params.exchangeId),
  });
}
